<script setup>

import {onMounted, reactive, watch} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";
import {userStore} from "@/Stores/UserStore";

const props = defineProps({
  status: String,
  personId: Number,
  updateNum: Number
});

const emit = defineEmits(['closed']);

const data = reactive({
  loading: true,
  dialog: true,
  roles: [],
  permissions: [],
  roleList: []
});

const close = () => {
  emit('closed');
}

const save = async () => {
  if(!data.loading) {
    data.loading = true;

    await ApiWrapper.post('user-management', 'update-user-roles', {
          personId: props.personId,
          roles: data.roles,
          permissions: data.permissions
        })
        .then((response) => {
          close();
          ToastUtil.successMsg('Roles updated for user');
        })
        .catch((e) => {
          ToastUtil.error(e);
        })
        .finally(() => {
          data.loading = false;
        });
  }
}

watch(() => props.updateNum, async (val) => {
  await ApiWrapper.post('user-management', 'get-user', {
    personId: props.personId
  })
  .then((response) => {
    data.roles = response.roles.map((role) => role.role);
    data.permissions = response.permissions;
  })
  .catch((e) => {
    ToastUtil.error(e);
  })
  .finally(()=>{
    data.loading = false;
  });
});

const loadRoles = async () => {
  data.loading = true;
  await ApiWrapper.get('auth-roles', 'list-roles').then((response) => {
    const isAdmin = userStore.userInfo?.isAdmin || false;
    data.roleList = response.roles.map((role) => {
      // If the user is not an admin and does not have a given role you shouldn't be able to assign/remove it
      if(!isAdmin && !userStore.userInfo?.roleIds.includes(role.id)) {
        role.props = { disabled: true};
      }
      // Add an * by default roles
      return {
        ...role,
        role: role.is_default ? `${role.role}` : role.role
      };
    });
  })
  .catch((e) => {
    ToastUtil.error(e);
  })
  .finally(()=>{
    data.loading = false;
  });
}

onMounted(async ()=>{
  await loadRoles();
});

</script>

<template>
  <div class="text-center">
    <v-dialog
        v-model="data.dialog"
        max-width="600"
        persistent
        v-if="props.status === 'open'"
        @click:outside="close"
    >
      <v-card>
        <v-toolbar flat color="white">
          <v-toolbar-title>Update User Roles</v-toolbar-title>
          <v-progress-linear
              :active="data.loading"
              :indeterminate="data.loading"
              absolute
              bottom
              color="primary"
          >
          </v-progress-linear>
        </v-toolbar>

        <v-card-text>
          <v-row>
            <v-col cols="12" sm="12">
              <v-select
                  v-model="data.roles"
                  :items="data.roleList"
                  label="Roles"
                  item-title="role"
                  item-value="role"
                  chips
                  multiple
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              text="Close"
              variant="plain"
              @click="close"
          ></v-btn>

          <v-btn
              color="primary"
              text="Update"
              variant="tonal"
              :disabled="data.loading"
              @click="save"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

</style>